import { useMemo } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { actionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { ISponsor } from 'src/interfaces/Sponsor';

interface SponsorsTableProps {
  sponsors: ISponsor[];
  onEdit: (sponsor: ISponsor) => void;
  onDelete: (id: string) => void;
}

const SponsorsTable = ({ sponsors, onEdit, onDelete }: SponsorsTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo(
    (): GridColDef<ISponsor>[] => [
      { field: 'name', headerName: t('system.sponsors.name'), width: 220, sortable: false },
      {
        field: 'url',
        headerName: t('system.sponsors.url'),
        flex: 1,
        minWidth: 200,
        sortable: false,
      },
      {
        field: 'published',
        headerName: t('system.sponsors.published'),
        width: 100,
        sortable: false,
        valueGetter: (_, row) => (row.published ? '✓' : '-'),
      },
      actionsColumn<ISponsor>(t, 'technicalTypes.optionsLabel', ({ row }) => (
        <>
          <Tooltip title={t('participants.edit')}>
            <IconButton size="small" onClick={() => onEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('participants.delete')}>
            <IconButton size="small" onClick={() => onDelete(row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )),
    ],
    [onDelete, onEdit, t],
  );

  return (
    <DashboardDataGrid<ISponsor>
      aria-label={t('system.sponsors.pageTitle')}
      chrome="compact"
      rows={sponsors}
      columns={columns}
    />
  );
};

export default SponsorsTable;
